import { creatorbase, creatorbaseQueries } from '@round/api';
import { Form, FormikProvider, useFormik, yupToFormErrors } from 'formik';
import { Modal } from '@round/whitelabel-ui';
import CampaignFormBase, {
    BaseCampaignFormValues,
    getBaseCampaignFormFields,
    mapCampaignApiErrorsToFormikErrors,
    mapCampaignFormValuesToApiData,
    sharedCampaignValidationSchema,
    validationSchema,
} from '../../components/CampaignFormBase/CampaignFormBase';
import { omit } from 'lodash';
import { useMemo } from 'react';
import useNonNullContext from 'Hooks/useNonNullContext';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { GenericDropdownOption } from 'App.types';
import { Button } from '@round/whitelabel-ui';
import useUpdateProjectCampaign from '../../hooks/useUpdateProjectCampaign';
import { getObjectDifference, showNotification } from 'helpers';
import { useAllPosts, useClient, useUsers } from '@round/creatorbase-helpers/api-hooks';
import { useCurrentCampaignPaymentDetails } from '../../components/ProjectCampaignsTable/CampaignPaymentRequestModal/useCurrentCampaignPaymentDetails';
import { mapUserToOption } from 'Modules/Plans/User/hooks/useUsersSelect';
import styles from './UpdateYoutubeCampaignModal.module.css';
import useProjectDetails from 'Modules/Plans/Project/ProjectDetails/useProjectDetails';
import { getProjectDetailsPostsParams } from 'Modules/Plans/Project/ProjectDetails/hooks/posts/getProjectDetailsPostsParams';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import { isCampaignCostLockedByPayment } from 'Modules/Plans/Campaigns/helpers';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    campaign: creatorbase.YoutubeCampaign | null;
};

const UpdateYoutubeCampaignModal = ({ isOpen, closeModal, campaign }: Props) => {
    const { update: updateCampaign } = useUpdateProjectCampaign();
    const { currencies } = useNonNullContext(OptionsContext);
    const currency = currencies
        .map<GenericDropdownOption<number>>((c) => ({ value: c.id, label: c.name }))
        .find((o) => o.value === campaign?.currency_id);

    const { team, project } = useProjectDetails();
    const { client } = useClient(team?.client_id);
    const isCampaignPaymentRequestsEnabled = useFeatureFlags().isEnabled('rou_539_campaign_payment_requests');
    const arePaymentsEnabled = isCampaignPaymentRequestsEnabled && !!client?.is_payment_enabled;
    const shouldFetchPaymentDetails = arePaymentsEnabled && isOpen && campaign?.is_post_cost_editable === false;
    const { campaignPaymentDetails } = useCurrentCampaignPaymentDetails(
        { campaignId: campaign?.id, paymentRequestId: campaign?.payment_request_id },
        { enabled: shouldFetchPaymentDetails }
    );
    const {
        data: campaignPosts,
        isLoading: arePostsLoading,
        hasError: postsHaveError,
    } = useAllPosts(getProjectDetailsPostsParams(campaign?.id ?? 0), { enabled: !!campaign && isOpen });

    const teamMembers = campaign?.team_members;
    const { data: users } = useUsers(
        { id: teamMembers?.join(), page_size: teamMembers?.length },
        { enabled: !!teamMembers?.length && isOpen }
    );

    const initialValues = useMemo<BaseCampaignFormValues>(
        () => ({
            cost: String((shouldFetchPaymentDetails ? campaignPaymentDetails?.base_cost : campaign?.cost) ?? ''),
            currency: currency,
            is_post_cost_editable: campaign?.is_post_cost_editable ?? null,
            name: campaign?.name ?? '',
            team_members: users?.results.map(mapUserToOption),
        }),
        [
            shouldFetchPaymentDetails,
            campaignPaymentDetails?.base_cost,
            campaign?.cost,
            campaign?.is_post_cost_editable,
            campaign?.name,
            currency,
            users?.results,
        ]
    );

    const formik = useFormik<BaseCampaignFormValues>({
        initialValues,
        onSubmit: (values, helpers) => {
            if (values.is_post_cost_editable === null || !campaign?.id) {
                helpers.setSubmitting(false);
                return;
            }

            const mappedFormValues = mapCampaignFormValuesToApiData(values, arePaymentsEnabled);
            const updatedFields = getObjectDifference(mappedFormValues, campaign);

            updateCampaign(
                { id: campaign.id, ...updatedFields },
                {
                    onSuccess: () => {
                        showNotification('Campaign updated', 'info');
                        helpers.resetForm();
                        closeModal();
                    },
                    onError: (error) => {
                        if (
                            creatorbaseQueries.campaigns.utils.update.isExpectedErrorResponse(error) &&
                            error.response.status === 400
                        ) {
                            helpers.setErrors(mapCampaignApiErrorsToFormikErrors(error.response.data));
                            return;
                        }

                        showNotification('Could not update campaign', 'error');
                    },
                    onSettled: () => {
                        helpers.setSubmitting(false);
                    },
                }
            );
        },
        enableReinitialize: true,
        // validationSchema param threw an error on validation
        validate: async (values) => {
            try {
                if (isNotManagedByCreatorbase) {
                    await sharedCampaignValidationSchema.validate(values, { abortEarly: false });
                    return;
                }

                const transformedValues = values.is_post_cost_editable === false ? values : omit(values, 'cost');
                await validationSchema.validate(transformedValues, { abortEarly: false });
            } catch (e) {
                return yupToFormErrors(e);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        closeModal();
    };

    // When the post-fetch fails, we can't confirm the campaign is free of
    // costed posts, so keep the spending type locked rather than letting it
    // silently fall open.
    const isSpendingTypeDisabled =
        campaign?.is_post_cost_editable &&
        (postsHaveError ||
            campaignPosts.some(
                (post) =>
                    post.cost !== null && !backupPostsStatuses.includes(post.status as creatorbase.BackupPostStatus)
            ));
    const isNotManagedByCreatorbase = Boolean(campaign?.managed_by && campaign?.managed_by !== 'creatorbase');
    const isPaymentLocked = isCampaignCostLockedByPayment(campaign?.payment_status);

    const readonlyFieldConditions: { [k in keyof BaseCampaignFormValues]?: boolean } = {
        is_post_cost_editable: isSpendingTypeDisabled || isNotManagedByCreatorbase || isPaymentLocked,
        cost: isNotManagedByCreatorbase || isPaymentLocked,
        currency:
            (project?.campaign_count && project.campaign_count > 1) || isNotManagedByCreatorbase || isPaymentLocked,
    };

    const readonlyFields = Object.entries(readonlyFieldConditions)
        .filter(([, condition]) => !!condition)
        .map(([field]) => field);

    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>Edit campaign</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormikProvider value={formik}>
                    <Form className={styles.form}>
                        <CampaignFormBase
                            readonlyFields={getBaseCampaignFormFields(readonlyFields)}
                            teamId={team?.id}
                            campaignId={campaign?.id}
                            paymentRequestId={campaign?.payment_request_id}
                            isManagedExternally={isNotManagedByCreatorbase}
                        />
                    </Form>
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    disabled={!formik.dirty || !formik.isValid || arePostsLoading}
                    isLoading={formik.isSubmitting || arePostsLoading}
                    onClick={formik.submitForm}
                >
                    Submit
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default UpdateYoutubeCampaignModal;
